import { CloudArrowUpIcon, DocumentIcon, XMarkIcon } from '@heroicons/react/24/outline';
import React from 'react';
import { FilesTooltip } from './config';
import DragAndDropZone from './DragAndDropZone';

interface UploadZoneContentProps {
    isUploading: boolean;
    uploadSuccess: boolean;
    uploadedCount: number;
    totalUploadCount: number;
    uploadProgress: number;
    currentUploadingFile: string;
    stagedFiles: File[];
    isDragOver: boolean;
    uploadError: string | null;
    onDrop: (e: React.DragEvent) => void;
    onDragOver: (e: React.DragEvent) => void;
    onDragLeave: (e: React.DragEvent) => void;
    onFileInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onRemoveFile: (index: number) => void;
    onClearAll: () => void;
    onUpload: () => void;
    onClearError: () => void;
}

const UploadZoneContent: React.FC<UploadZoneContentProps> = ({
    isUploading,
    uploadSuccess,
    uploadedCount,
    totalUploadCount,
    uploadProgress,
    currentUploadingFile,
    stagedFiles,
    isDragOver,
    uploadError,
    onDrop,
    onDragOver,
    onDragLeave,
    onFileInputChange,
    onRemoveFile,
    onClearAll,
    onUpload,
    onClearError
}) => {
    if (isUploading) {
        return (
            <div className="py-6 px-4 bg-gray-50 rounded-xl border border-gray-200">
                <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                        <div className="w-5 h-5 rounded-full border-2 border-gray-300 border-t-gray-800 animate-spin" />
                        <span className="text-sm text-gray-700 font-medium">
                            Uploading {uploadedCount}/{totalUploadCount}
                        </span>
                    </div>
                    <span className="text-sm text-gray-500 font-medium">
                        {Math.round(uploadProgress)}%
                    </span>
                </div>
                {currentUploadingFile && (
                    <FilesTooltip content={currentUploadingFile}>
                        <p className="text-xs text-gray-500 mb-2 truncate">
                            {currentUploadingFile}
                        </p>
                    </FilesTooltip>
                )}
                <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                    <div
                        className="bg-gray-800 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${Math.round(uploadProgress)}%` }}
                    />
                </div>
            </div>
        );
    }

    if (uploadSuccess) {
        return (
            <div className="flex items-center justify-center py-8 bg-green-50 rounded-xl border border-green-200">
                <div className="flex-shrink-0 w-6 h-6 text-green-500 mr-3">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path
                            fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z"
                            clipRule="evenodd"
                        />
                    </svg>
                </div>
                <span className="text-sm text-green-800 font-medium">
                    Files uploaded successfully!
                </span>
            </div>
        );
    }

    return (
        <>
            <DragAndDropZone
                onDrop={onDrop}
                onDragOver={onDragOver}
                onDragLeave={onDragLeave}
                onFileInputChange={onFileInputChange}
                isDragOver={isDragOver}
                accept="*/*"
                showMaxSize={false}
            />

            {stagedFiles.length > 0 && (
                <div className="space-y-2">
                    <div className="flex items-center justify-between">
                        <span className="text-xs font-medium text-gray-600">
                            {stagedFiles.length} {stagedFiles.length === 1 ? 'file' : 'files'} ready
                        </span>
                        <button
                            onClick={onClearAll}
                            className="text-xs text-gray-500 hover:text-gray-700 transition-colors"
                        >
                            Clear all
                        </button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {stagedFiles.map((file, index) => {
                            const fileSizeMB = (file.size / (1024 * 1024)).toFixed(1);
                            const fileSizeKB = (file.size / 1024).toFixed(0);
                            const displaySize =
                                file.size >= 1024 * 1024 ? `${fileSizeMB}MB` : `${fileSizeKB}KB`;

                            return (
                                <div
                                    key={`${file.name}-${file.size}-${file.lastModified}`}
                                    className="inline-flex items-center gap-1.5 bg-white rounded-lg px-2.5 py-1.5 border border-gray-200 group hover:border-gray-300 transition-colors max-w-xs"
                                >
                                    <DocumentIcon className="w-4 h-4 flex-shrink-0 text-gray-400" />
                                    <div className="flex-1 min-w-0">
                                        <FilesTooltip content={file.name}>
                                            <p className="text-xs text-gray-700 truncate font-medium">
                                                {file.name}
                                            </p>
                                        </FilesTooltip>
                                    </div>
                                    <span className="text-xs flex-shrink-0 text-gray-400">
                                        {displaySize}
                                    </span>
                                    <FilesTooltip content="Remove">
                                        <button
                                            onClick={() => onRemoveFile(index)}
                                            className="flex-shrink-0 text-gray-400 hover:text-red-600 transition-colors opacity-0 group-hover:opacity-100"
                                        >
                                            <XMarkIcon className="w-4 h-4" />
                                        </button>
                                    </FilesTooltip>
                                </div>
                            );
                        })}
                    </div>
                    <button
                        onClick={onUpload}
                        className="w-full inline-flex items-center justify-center gap-2 bg-gray-900 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors font-medium text-sm"
                    >
                        <CloudArrowUpIcon className="w-4 h-4" />
                        Upload {stagedFiles.length} {stagedFiles.length === 1 ? 'file' : 'files'}
                    </button>
                </div>
            )}

            {uploadError && (
                <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex-shrink-0 w-5 h-5 text-red-500 mt-0.5">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                        >
                            <path
                                fillRule="evenodd"
                                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z"
                                clipRule="evenodd"
                            />
                        </svg>
                    </div>
                    <div className="flex-1">
                        <p className="text-sm text-red-800 font-medium">{uploadError}</p>
                    </div>
                    <button
                        onClick={onClearError}
                        className="flex-shrink-0 text-red-400 hover:text-red-600 transition-colors"
                    >
                        <XMarkIcon className="w-4 h-4" />
                    </button>
                </div>
            )}
        </>
    );
};

export { UploadZoneContent };
